import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import supportService from "../../api/services/supportService";
import {
  getSupportSocket,
  joinSupportTicketRoom,
  leaveSupportTicketRoom,
} from "../../api/services/supportSocket";
import toast from "react-hot-toast";
import { confirmDanger } from "../../utils/confirmDialog";
import { FiHeadphones, FiMic, FiPaperclip, FiPhone, FiPhoneOff, FiPlay, FiRefreshCw, FiSend, FiTrash2, FiX } from "react-icons/fi";
import { MdOutlineSupportAgent } from "react-icons/md";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import VoiceNote from "../../components/VoiceNote";
import LiveRecordingWaveform from "../../components/LiveRecordingWaveform";
import ChatMediaLightbox, { isImageMedia, isVideoMedia } from "../../components/ChatMediaLightbox";
import MediaSendConfirmModal from "../../components/MediaSendConfirmModal";
import SupportCallModal from "../../components/SupportCallModal";
import SupportPerformanceTab from "./SupportPerformanceTab";
import { ensureMicrophonePermission, joinSupportAgoraCall, leaveSupportAgoraCall, parseSupportAgoraSession } from "../../utils/supportAgoraCall";
import {
  startSupportCallRingback,
  stopSupportCallRingback,
  disposeSupportCallRingback,
} from "../../utils/supportCallRingback";
import { ListSkeleton, ChatSkeleton } from "../../components/common/skeletons";

const THEME = { primary: "#FB565F" };
const NASEEBI_AGENT_AVATAR = require("../../assests/logo.png");
const STATUS_OPTIONS = ["PENDING", "IN_PROGRESS", "COMPLETED", "RESOLVED", "CLOSED"];

function ticketUnreadCount(t) {
  if (!t) return 0;
  const n = Number(t.unreadCount) || Number(t.unread_agent_count) || 0;
  if (n > 0) return n;
  return !t.agent_seen ? 1 : 0;
}

const STATUS_STYLES = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  IN_PROGRESS: "bg-blue-100 text-blue-800 border-blue-200",
  COMPLETED: "bg-purple-100 text-purple-800 border-purple-200",
  RESOLVED: "bg-green-100 text-green-800 border-green-200",
  CLOSED: "bg-gray-100 text-gray-600 border-gray-200",
};

function formatRecordingTime(seconds) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
        STATUS_STYLES[status] || STATUS_STYLES.CLOSED
      }`}
    >
      {status?.replace("_", " ")}
    </span>
  );
}

function PresenceBadge({ sessionType, isOnline }) {
  if (sessionType === "GUEST" || isOnline == null) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium border ${
        isOnline
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-gray-50 text-gray-500 border-gray-200"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-green-500" : "bg-gray-400"}`}
      />
      {isOnline ? "Online" : "Offline"}
    </span>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`font-main text-sm font-semibold px-5 py-2.5 rounded-xl transition-all ${
        active
          ? "text-white shadow-md"
          : "text-gray-600 hover:bg-gray-100 bg-white border border-gray-200"
      }`}
      style={active ? { backgroundColor: THEME.primary } : {}}
    >
      {children}
    </button>
  );
}

export default function Support() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAdmin = userRole === "Admin" || userRole === "Developer";
  const isDeveloper = userRole === "Developer";
  const isCsrTeamLead = userRole === "CSR Team Lead";
  const isChatSupport = userRole === "Chat Support";

  const currentAdminId = (() => {
    try {
      const raw = localStorage.getItem("adminUserDetails");
      if (!raw) return null;
      return JSON.parse(raw)?.id ?? null;
    } catch {
      return null;
    }
  })();

  const deepLinkTicketId = Number(searchParams.get("ticketId") || 0) || null;
  const deepLinkView = searchParams.get("view");

  const [mainTab, setMainTab] = useState("tickets");
  const [ticketView, setTicketView] = useState(
    deepLinkView === "available" || deepLinkView === "mine" || deepLinkView === "team" || deepLinkView === "all"
      ? deepLinkView
      : isAdmin
        ? "all"
        : "available"
  );
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [canWork, setCanWork] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [tabPermissions, setTabPermissions] = useState(null);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({ session_type: "", status: "", search: "" });
  const [access, setAccess] = useState({ isTeamLead: false });
  const [activeCall, setActiveCall] = useState(null);
  const [callPhase, setCallPhase] = useState("idle"); // idle | connecting | ringing | joining | connected
  const [callDuration, setCallDuration] = useState(0);
  const [callMinimized, setCallMinimized] = useState(false);
  const [endingCall, setEndingCall] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [startingCall, setStartingCall] = useState(false);
  const [performance, setPerformance] = useState([]);
  const [performanceScope, setPerformanceScope] = useState("all");
  const [performanceViewScope, setPerformanceViewScope] = useState("all");
  const [performanceKind, setPerformanceKind] = useState("csr");
  const [perfLedTeams, setPerfLedTeams] = useState([]);
  const [perfTeamId, setPerfTeamId] = useState(null);
  const [deletingTicketId, setDeletingTicketId] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null); // { url, kind: 'image'|'video', title? }
  const [pendingAttachFile, setPendingAttachFile] = useState(null);

  const callClientRef = useRef(null);
  const callStartRef = useRef(0);
  const customerJoinedRef = useRef(false);
  const callSessionRef = useRef(0); // increments to cancel in-flight start
  const bottomRef = useRef(null);
  const attachInputRef = useRef(null);
  const selectedRef = useRef(selected);
  const activeCallRef = useRef(activeCall);
  const callPhaseRef = useRef(callPhase);
  const lastCallEndHandledRef = useRef({ callId: null, at: 0 });

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    if (selected?.id) {
      window.__naseebiOpenTicketId = selected.id;
    } else {
      window.__naseebiOpenTicketId = null;
    }
    return () => {
      if (
        selected?.id != null &&
        window.__naseebiOpenTicketId != null &&
        String(window.__naseebiOpenTicketId) === String(selected.id)
      ) {
        window.__naseebiOpenTicketId = null;
      }
    };
  }, [selected?.id]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    callPhaseRef.current = callPhase;
  }, [callPhase]);

  const resetCallUi = useCallback(() => {
    stopSupportCallRingback();
    customerJoinedRef.current = false;
    setCallPhase("idle");
    setCallDuration(0);
    setCallMinimized(false);
    setEndingCall(false);
    callStartRef.current = 0;
  }, []);

  const handleCustomerJoinedCall = useCallback(() => {
    if (customerJoinedRef.current) return;
    customerJoinedRef.current = true;
    stopSupportCallRingback();
    setCallPhase("connected");
    if (!callStartRef.current) callStartRef.current = Date.now();
    toast.success("Customer joined the call");
  }, []);

  const handleCustomerAcceptedCall = useCallback(() => {
    if (customerJoinedRef.current) return;
    stopSupportCallRingback();
    setCallMinimized(false);
    setCallPhase("joining");
  }, []);

  const loadAccess = useCallback(async () => {
    try {
      const data = await supportService.getAccess();
      const perms = data.access?.tabPermissions || null;
      setTabPermissions(perms);
      if (data.access) {
        const { storeSupportAccessMeta } = await import(
          "../../constants/supportTabPermissions"
        );
        storeSupportAccessMeta(data.access);
      }
      if (data.access) {
        setAccess((prev) => ({ ...prev, ...data.access }));
      }
    } catch {
      // CSR without support access may get 403 — tabs fall back to tickets only
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supportService.getTickets({ ...filters, view: ticketView });
      setTickets((data.tickets || []).map((t) => ({
        ...t,
        unreadCount: ticketUnreadCount(t),
      })));
      if (data.access) setAccess(data.access);
    } catch {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [filters, ticketView]);

  const loadPerformance = useCallback(async (teamId = perfTeamId) => {
    setPerformanceLoading(true);
    try {
      const params = teamId ? { team_id: teamId } : {};
      const data = await supportService.getPerformance(params);
      const scope = data.access?.scope;
      const kind = data.performanceKind || "csr";
      const chatSupportView =
        (data.access?.isChatSupport && scope === "self") || kind === "chat_support";
      setPerformance(data.performance || []);
      setPerformanceKind(chatSupportView ? "chat_support" : kind);
      if (data.ledTeams?.length) {
        setPerfLedTeams(data.ledTeams);
        // Team leads default into their first team; Admin/Developer stay on "All".
        if (scope === "teams" && !teamId && data.teamId) {
          setPerfTeamId(data.teamId);
        } else if (teamId) {
          setPerfTeamId(teamId);
        } else if (scope === "all") {
          setPerfTeamId(null);
        }
      } else if (data.teamId && scope !== "self" && scope !== "all") {
        setPerfTeamId(data.teamId);
      } else if (scope === "self" || scope === "all") {
        setPerfTeamId(null);
      }
      setPerformanceViewScope(scope || "all");
      const teamName = data.teamName;
      if (scope === "self") setPerformanceScope("Your performance");
      else if (scope === "teams" && teamName) {
        setPerformanceScope(teamName);
      } else if (scope === "teams") setPerformanceScope("Your team");
      else if (scope === "team") setPerformanceScope("Your team");
      else if (scope === "all") setPerformanceScope("All employees (team leads + teams)");
      else setPerformanceScope("All support agents");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to load performance";
      toast.error(msg);
    } finally {
      setPerformanceLoading(false);
    }
  }, [perfTeamId]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  const canViewPerformance = isAdmin || tabPermissions?.performance !== false;
  const canInteractWithTicket = canWork && !isAdmin;

  const patchTicketInList = useCallback((ticket) => {
    if (!ticket?.id) return;
    setTickets((prev) => {
      const idx = prev.findIndex((t) => Number(t.id) === Number(ticket.id));
      if (idx === -1) return [ticket, ...prev];
      const next = [...prev];
      next[idx] = { ...next[idx], ...ticket };
      return next.sort(
        (a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
      );
    });
  }, []);

  const removeTicketFromList = useCallback((ticketId) => {
    if (!ticketId) return;
    setTickets((prev) => prev.filter((t) => Number(t.id) !== Number(ticketId)));
  }, []);

  const enrichIncomingTicket = useCallback(
    (ticket) => {
      // Match backend canReadTicketChat: unassigned PENDING is readable (so Claim works
      // on socket-inserted rows without a list reload).
      const assignedId = ticket.assigned_agent_id
        ? Number(ticket.assigned_agent_id)
        : null;
      const canRead = isAdmin
        ? true
        : ticket.status === "CLOSED"
          ? false
          : !assignedId
            ? ticket.status === "PENDING"
            : assignedId === Number(currentAdminId);
      return {
        ...ticket,
        unreadCount: ticketUnreadCount(ticket),
        canClaim: ticket.status === "PENDING" && !ticket.assigned_agent_id,
        canWork:
          !isAdmin &&
          ticket.status !== "CLOSED" &&
          assignedId === Number(currentAdminId),
        canRead,
      };
    },
    [currentAdminId, isAdmin]
  );

  const canReadTicket = useCallback(
    (ticket) => {
      if (!ticket) return false;
      if (isAdmin) return true;
      if (ticket.status === "CLOSED") return false;
      if (!ticket.assigned_agent_id) return ticket.status === "PENDING";
      return Number(ticket.assigned_agent_id) === Number(currentAdminId);
    },
    [isAdmin, currentAdminId]
  );

  const ticketMatchesView = useCallback(
    (ticket) => {
      if (!ticket) return false;
      if (filters.status && ticket.status !== filters.status) return false;
      if (filters.session_type && ticket.session_type !== filters.session_type) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const hay = [
          ticket.displayName,
          ticket.guest_name,
          ticket.guest_email,
          ticket.guest_phone,
          String(ticket.id),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const unassigned = !ticket.assigned_agent_id;
      if (ticketView === "available") return ticket.status === "PENDING" && unassigned;
      if (ticketView === "mine") {
        return Number(ticket.assigned_agent_id) === Number(currentAdminId);
      }
      if (ticketView === "team") {
        const agentId = ticket.assigned_agent_id ? Number(ticket.assigned_agent_id) : null;
        if (!agentId) return false;
        const scope = (access.teamScopeIds || []).map(Number).filter(Boolean);
        if (scope.length) return scope.includes(agentId);
        // Admin "Team" without a lead scope: any assigned ticket.
        if (isAdmin) return true;
        return agentId === Number(currentAdminId);
      }
      return true;
    },
    [filters, ticketView, currentAdminId, access.teamScopeIds, isAdmin]
  );

  const applyTicketToList = useCallback(
    (ticket) => {
      if (!ticket?.id) return null;
      const enriched = enrichIncomingTicket(ticket);
      if (ticketMatchesView(enriched)) patchTicketInList(enriched);
      else removeTicketFromList(enriched.id);
      return enriched;
    },
    [enrichIncomingTicket, ticketMatchesView, patchTicketInList, removeTicketFromList]
  );

  const messagePreview = (msg) => {
    if (msg.type === "IMAGE") return "Sent an image";
    if (msg.type === "VOICE") return "Sent a voice message";
    if (msg.type === "ATTACHMENT") return msg.message ? `Attachment: ${msg.message}` : "Sent an attachment";
    if (msg.type === "CALL") return "Support call";
    return msg.message || "New message";
  };

  const patchTicketFromMessage = useCallback(
    (msg) => {
      if (!msg?.request_id) return;
      const isOpen = Number(selected?.id) === Number(msg.request_id);
      const fromCustomer = !msg.isAgent && msg.user_type !== "AGENT" && msg.user_type !== "CSR";
      setTickets((prev) => {
        const idx = prev.findIndex((t) => Number(t.id) === Number(msg.request_id));
        if (idx === -1) return prev;
        const next = [...prev];
        const cur = next[idx];
        let unreadCount = ticketUnreadCount(cur);
        if (isOpen) {
          unreadCount = 0;
        } else if (fromCustomer) {
          unreadCount =
            msg.unreadCount != null
              ? Number(msg.unreadCount) || 0
              : unreadCount + 1;
        }
        next[idx] = {
          ...cur,
          last_message: messagePreview(msg),
          last_message_at: msg.created_at || new Date().toISOString(),
          agent_seen: isOpen ? 1 : fromCustomer ? 0 : cur.agent_seen,
          unreadCount,
          unread_agent_count: unreadCount,
        };
        return next.sort(
          (a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
        );
      });
      if (isOpen && fromCustomer) {
        supportService.markTicketSeen(msg.request_id).catch(() => {});
      }
    },
    [selected?.id]
  );

  const openTicket = async (id, claim = false) => {
    setTicketLoading(true);
    const stub = ticketsRef.current?.find((t) => t.id === id);
    // Claiming an unassigned PENDING ticket is always allowed; don't block on stale canRead.
    const claimingAvailable =
      claim && stub && stub.status === "PENDING" && !stub.assigned_agent_id;
    if (stub && !claimingAvailable && !canReadTicket(stub)) {
      toast.error(
        stub.status === "CLOSED"
          ? "Only Admin or Developer can view closed ticket chats."
          : stub.assigned_agent_id &&
              Number(stub.assigned_agent_id) !== Number(currentAdminId)
            ? "Only Admin or Developer can view another agent's ticket chat."
            : "You do not have access to this ticket."
      );
      setTicketLoading(false);
      return;
    }
    if (stub) {
      setSelected(stub);
      setMessages([]);
      setCanWork(false);
    }
    try {
      if (claim) {
        const claimRes = await supportService.claimTicket(id);
        if (claimRes.ticket) applyTicketToList(claimRes.ticket);
        toast.success("Ticket claimed!");
      }
      const data = await supportService.getTicket(id);
      if (data?.ticket && !canReadTicket(data.ticket)) {
        toast.error(
          data.ticket.status === "CLOSED"
            ? "Only Admin or Developer can view closed ticket chats."
            : data.ticket.assigned_agent_id &&
                Number(data.ticket.assigned_agent_id) !== Number(currentAdminId)
              ? "Only Admin or Developer can view another agent's ticket chat."
              : "You do not have access to this ticket."
        );
        if (selectedRef.current?.id) leaveSupportTicketRoom(selectedRef.current.id);
        setSelected(null);
        setMessages([]);
        setCanWork(false);
        setTicketLoading(false);
        return;
      }
      if (data?.ticket) applyTicketToList(data.ticket);
      setSelected(data.ticket);
      setCanWork(!!data.ticket?.canWork && !isAdmin);
      setMessages(data.details || []);
      joinSupportTicketRoom(id);
    } catch (e) {
      if (stub) {
        setSelected(null);
        setMessages([]);
        setCanWork(false);
      }
      toast.error(e?.response?.data?.message || "Failed to open ticket");
    } finally {
      setTicketLoading(false);
    }
  };

  const deleteTicket = async (ticket, e) => {
    e?.stopPropagation?.();
    const label = ticket.displayName || ticket.guest_name || `Ticket #${ticket.id}`;
    const ok = await confirmDanger(
      `Delete support ticket for ${label}? All messages and call history will be permanently removed.`,
      "Delete support ticket?"
    );
    if (!ok) return;
    try {
      setDeletingTicketId(ticket.id);
      await supportService.deleteTicket(ticket.id);
      if (selected?.id === ticket.id) {
        if (selected?.id) leaveSupportTicketRoom(selected.id);
        setSelected(null);
        setMessages([]);
        setCanWork(false);
      }
      toast.success("Ticket deleted");
      loadTickets();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        "Failed to delete ticket";
      toast.error(msg);
    } finally {
      setDeletingTicketId(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (mainTab === "tickets") loadTickets();
    if (mainTab === "performance" && canViewPerformance) loadPerformance();
    if (mainTab === "performance" && !canViewPerformance && tabPermissions !== null) {
      setMainTab("tickets");
    }
  }, [mainTab, loadTickets, canViewPerformance, tabPermissions]);

  // Deep-link from admin notifications: /support?ticketId=123&view=available
  const deepLinkHandledRef = useRef(null);
  useEffect(() => {
    if (!deepLinkTicketId || loading || ticketLoading) return;
    if (deepLinkHandledRef.current === deepLinkTicketId) return;
    deepLinkHandledRef.current = deepLinkTicketId;
    setMainTab("tickets");
    if (deepLinkView === "available" || deepLinkView === "mine" || deepLinkView === "team" || deepLinkView === "all") {
      setTicketView(deepLinkView);
    }
    openTicket(deepLinkTicketId, false).finally(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("ticketId");
      setSearchParams(next, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTicketId, loading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const ticketsRef = useRef(tickets);
  ticketsRef.current = tickets;

  useEffect(() => {
    const socket = getSupportSocket();
    const onNew = (ticket) => {
      if (!ticket?.id) {
        loadTickets();
        return;
      }
      const enriched = enrichIncomingTicket(ticket);
      if (!ticketMatchesView(enriched)) return;
      patchTicketInList(enriched);
      // Toast / browser alert handled globally by AdminNotificationsDropdown.
    };
    const onMsg = (msg) => {
      const sel = selectedRef.current;
      if (sel && Number(msg.request_id) === Number(sel.id)) {
        setMessages((prev) => {
          const existing = prev.find((m) => m.id === msg.id);
          if (existing) {
            return prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m));
          }
          return [...prev, msg];
        });
      }
      patchTicketFromMessage(msg);
    };
    const onCallEnd = async (payload) => {
      const requestId = Number(payload?.requestId);
      const callId = Number(payload?.callId);
      const sel = selectedRef.current;
      const active = activeCallRef.current;
      const ticketMatch = sel && requestId === Number(sel.id);
      const callMatch = active && callId && Number(active.callId) === callId;
      if (!ticketMatch && !callMatch) return;

      const now = Date.now();
      if (
        callId &&
        lastCallEndHandledRef.current.callId === callId &&
        now - lastCallEndHandledRef.current.at < 5000
      ) {
        return;
      }
      lastCallEndHandledRef.current = { callId: callId || null, at: now };

      if (callClientRef.current) {
        await leaveSupportAgoraCall(
          callClientRef.current,
          callClientRef.current?.localAudioTrack
        );
        callClientRef.current = null;
      }

      const who = sel?.displayName || sel?.guest_name || "Customer";
      if (payload?.declined) {
        toast.error(`${who} declined the call`);
      } else if (active || callPhaseRef.current !== "idle") {
        toast(`${who} ended the call`, { icon: "📞" });
      }

      resetCallUi();
      setActiveCall(null);

      if (payload?.message && ticketMatch) {
        setMessages((prev) => {
          const existing = prev.find((m) => m.id === payload.message.id);
          if (existing) {
            return prev.map((m) =>
              m.id === payload.message.id ? { ...m, ...payload.message } : m
            );
          }
          return [...prev, payload.message];
        });
      }
    };
    const onCallAnswered = (payload) => {
      const requestId = Number(payload?.requestId);
      const callId = Number(payload?.callId);
      const sel = selectedRef.current;
      const active = activeCallRef.current;
      const ticketMatch = sel && requestId === Number(sel.id);
      const callMatch = active && callId && Number(active.callId) === callId;
      if (!ticketMatch && !callMatch) return;

      handleCustomerAcceptedCall();

      if (payload?.message && ticketMatch) {
        setMessages((prev) => {
          const existing = prev.find((m) => m.id === payload.message.id);
          if (existing) {
            return prev.map((m) =>
              m.id === payload.message.id ? { ...m, ...payload.message } : m
            );
          }
          return [...prev, payload.message];
        });
      }
    };
    const onTicketUpdate = (ticket) => {
      if (!ticket?.id) return;
      const enriched = enrichIncomingTicket(ticket);
      const inList = ticketsRef.current.some((t) => Number(t.id) === Number(ticket.id));
      const matchesNow = ticketMatchesView(enriched);
      if (matchesNow) {
        patchTicketInList(enriched);
      } else if (inList) {
        // Status changed so it no longer belongs in this view (e.g. CLOSED) — remove live.
        removeTicketFromList(ticket.id);
      }

      const sel = selectedRef.current;
      if (sel && Number(ticket?.id) === Number(sel.id)) {
        if (!isAdmin && !canReadTicket(enriched)) {
          toast.error("This ticket was claimed by another agent.");
          leaveSupportTicketRoom(sel.id);
          setSelected(null);
          setMessages([]);
          setCanWork(false);
          return;
        }
        if (enriched.status === "CLOSED") {
          if (!isAdmin) {
            toast.success("Ticket closed");
            leaveSupportTicketRoom(sel.id);
            setSelected(null);
            setMessages([]);
            setCanWork(false);
          } else {
            setSelected((prev) => (prev ? { ...prev, ...enriched } : prev));
            setCanWork(false);
          }
        } else {
          setSelected((prev) => (prev ? { ...prev, ...enriched } : prev));
          setCanWork(!!enriched.canWork);
        }
      }
    };
    const onMessageDeleted = (payload) => {
      const requestId = Number(payload?.requestId);
      const messageId = Number(payload?.messageId);
      if (!Number.isFinite(messageId)) return;
      const sel = selectedRef.current;
      if (sel && requestId === Number(sel.id)) {
        setMessages((prev) => prev.filter((m) => Number(m.id) !== messageId));
      }
    };
    const onUserPresence = (payload) => {
      const userId = Number(payload?.userId);
      if (!Number.isFinite(userId)) return;
      const isOnline = !!(payload?.isOnline ?? payload?.isActive);
      setTickets((prev) =>
        prev.map((t) =>
          t.session_type === "AUTHENTICATED" && Number(t.user) === userId
            ? { ...t, isOnline }
            : t
        )
      );
      setSelected((prev) =>
        prev && prev.session_type === "AUTHENTICATED" && Number(prev.user) === userId
          ? { ...prev, isOnline }
          : prev
      );
    };
    socket.on("supportTicketNew", onNew);
    socket.on("supportMessage", onMsg);
    socket.on("supportMessageDeleted", onMessageDeleted);
    socket.on("supportCallEnded", onCallEnd);
    socket.on("supportCallAnswered", onCallAnswered);
    socket.on("supportTicketStatusUpdated", onTicketUpdate);
    socket.on("supportTicketAssigned", onTicketUpdate);
    socket.on("supportUserPresence", onUserPresence);
    return () => {
      socket.off("supportTicketNew", onNew);
      socket.off("supportMessage", onMsg);
      socket.off("supportMessageDeleted", onMessageDeleted);
      socket.off("supportCallEnded", onCallEnd);
      socket.off("supportCallAnswered", onCallAnswered);
      socket.off("supportTicketStatusUpdated", onTicketUpdate);
      socket.off("supportTicketAssigned", onTicketUpdate);
      socket.off("supportUserPresence", onUserPresence);
    };
  }, [
    loadTickets,
    patchTicketInList,
    patchTicketFromMessage,
    ticketMatchesView,
    enrichIncomingTicket,
    resetCallUi,
    removeTicketFromList,
    isAdmin,
    canReadTicket,
    handleCustomerJoinedCall,
    handleCustomerAcceptedCall,
  ]);

  useEffect(() => {
    if (callPhase !== "connected" || !callStartRef.current) {
      setCallDuration(0);
      return undefined;
    }
    const tick = () => {
      setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [callPhase]);

  useEffect(() => () => disposeSupportCallRingback(), []);

  useEffect(
    () => () => {
      if (selected?.id) leaveSupportTicketRoom(selected.id);
    },
    [selected?.id]
  );

  const sendMessage = async () => {
    if (!selected || !text.trim() || !canInteractWithTicket || sending) return;
    const payload = text.trim();
    setSending(true);
    setText("");
    try {
      const res = await supportService.sendMessage(selected.id, payload);
      if (res.message) {
        setMessages((prev) =>
          prev.some((m) => m.id === res.message.id) ? prev : [...prev, res.message]
        );
      }
    } catch (e) {
      setText(payload);
      toast.error(e?.response?.data?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const sendVoiceMessage = useCallback(
    async (blob, meta) => {
      if (!selected || !canInteractWithTicket) return;
      setSending(true);
      try {
        const res = await supportService.sendVoice(selected.id, blob, meta);
        if (res.message) {
          const message = {
            ...res.message,
            voice_waveform: res.message.voice_waveform ?? meta?.waveform,
            voice_duration_ms: res.message.voice_duration_ms ?? meta?.durationMs,
          };
          setMessages((prev) =>
            prev.some((m) => m.id === message.id) ? prev : [...prev, message]
          );
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to send voice message");
      } finally {
        setSending(false);
      }
    },
    [selected, canInteractWithTicket]
  );

  const sendAttachmentFile = useCallback(
    async (file) => {
      if (!selected || !canInteractWithTicket || !file || sending) return;
      setSending(true);
      try {
        const isImage = String(file.type || "").startsWith("image/");
        const res = isImage
          ? await supportService.sendImage(selected.id, file)
          : await supportService.sendAttachment(selected.id, file);
        if (res.message) {
          setMessages((prev) =>
            prev.some((m) => m.id === res.message.id) ? prev : [...prev, res.message]
          );
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.response?.data?.error || "Failed to send attachment");
      } finally {
        setSending(false);
      }
    },
    [selected, canInteractWithTicket, sending]
  );

  const {
    recording,
    recordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
    isFinalizing,
    waveBars,
  } = useVoiceRecorder(sendVoiceMessage, {
    onRecordingTooShort: () => toast.error("Recording too short"),
    onRecordingCancelled: () => {},
  });

  const onAttachClick = () => {
    if (!canInteractWithTicket || sending || recording || isFinalizing || ticketLoading) return;
    attachInputRef.current?.click();
  };

  const onAttachSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const maxBytes = 15 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("File is too large (max 15 MB)");
      return;
    }
    const isMedia =
      String(file.type || "").startsWith("image/") ||
      String(file.type || "").startsWith("video/");
    if (isMedia) {
      setPendingAttachFile(file);
      return;
    }
    await sendAttachmentFile(file);
  };

  const startCall = async () => {
    if (!selected || !canInteractWithTicket || startingCall || activeCall) return;
    if (selected.session_type === "AUTHENTICATED" && selected.user == null) {
      toast.error("User is logged out. You can only call logged-in users.");
      return;
    }

    // Verify microphone permission before creating a call on the server.
    const micPermission = await ensureMicrophonePermission();
    if (!micPermission.granted) {
      if (micPermission.reason === "no-device") {
        toast.error("No microphone found. Please connect a microphone and try again.");
      } else if (micPermission.reason === "unsupported") {
        toast.error("Microphone is not supported in this browser.");
      } else {
        toast.error(
          "Please allow microphone access first, then start the call again. Enable it from your browser's site permissions."
        );
      }
      return;
    }

    const ticketId = selected.id;
    const sessionId = ++callSessionRef.current;
    const isCurrentSession = () => callSessionRef.current === sessionId;

    setStartingCall(true);
    customerJoinedRef.current = false;
    callStartRef.current = 0;
    // Show admin modal FIRST — only then notify customer / join Agora.
    setActiveCall({ pending: true, callId: null, requestId: ticketId });
    setCallPhase("connecting");

    // Let React paint the modal before hitting the network.
    await new Promise((resolve) => {
      requestAnimationFrame(() => setTimeout(resolve, 50));
    });
    if (!isCurrentSession()) {
      setStartingCall(false);
      return;
    }

    try {
      const res = await supportService.initiateCall(ticketId, "voice");
      if (!isCurrentSession()) {
        // Agent cancelled while initiating — hang up server-side if call was created.
        try {
          if (res?.callId) await supportService.endCall(ticketId, res.callId, 0);
        } catch {
          // ignore
        }
        return;
      }

      const session = parseSupportAgoraSession(res);
      setActiveCall(res);
      setCallPhase("ringing");
      await startSupportCallRingback();

      const { client, localAudioTrack } = await joinSupportAgoraCall(session, {
        onRemoteUserJoined: handleCustomerJoinedCall,
      });

      if (!isCurrentSession()) {
        await leaveSupportAgoraCall(client, localAudioTrack);
        try {
          if (res?.callId) await supportService.endCall(ticketId, res.callId, 0);
        } catch {
          // ignore
        }
        return;
      }

      callClientRef.current = client;
      callClientRef.current.localAudioTrack = localAudioTrack;
    } catch (e) {
      if (isCurrentSession()) {
        await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
        callClientRef.current = null;
        resetCallUi();
        setActiveCall(null);
        toast.error(e?.response?.data?.message || e?.message || "Failed to start call");
      }
    } finally {
      if (isCurrentSession()) setStartingCall(false);
    }
  };

  const endCall = async () => {
    if (!selected || !activeCall || endingCall) return;
    setEndingCall(true);
    // Cancel in-flight start (modal Cancel during "connecting")
    callSessionRef.current += 1;
    stopSupportCallRingback();

    const sel = selectedRef.current;
    const active = activeCallRef.current;
    const duration = callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0;

    try {
      if (sel && active?.callId) {
        await supportService.endCall(sel.id, active.callId, duration);
      }
      await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
    } catch {
      // ignore
    }
    setActiveCall(null);
    callClientRef.current = null;
    setStartingCall(false);
    resetCallUi();
  };

  const updateStatus = async (status) => {
    if (!selected || !canInteractWithTicket || updatingStatus) return;
    setUpdatingStatus(status);
    try {
      const res = await supportService.updateStatus(selected.id, status);
      const enriched = applyTicketToList(res.ticket) || enrichIncomingTicket(res.ticket);
      setSelected(enriched);
      toast.success(`Status: ${status.replace("_", " ")}`);
      if (status === "CLOSED") setCanWork(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const deleteMessage = async (m) => {
    if (!selected || !isAdmin || selected.status === "CLOSED" || !m?.id || m.type === "CALL") return;
    const ok = await confirmDanger(
      "This will permanently remove the message from the chat for everyone.",
      "Delete this message?"
    );
    if (!ok) return;
    try {
      await supportService.deleteMessage(selected.id, m.id);
      setMessages((prev) => prev.filter((x) => Number(x.id) !== Number(m.id)));
      toast.success("Message deleted");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to delete message");
    }
  };

  const openMediaPreview = (url, kind, title) => {
    if (!url) return;
    setMediaPreview({ url, kind, title: title || "" });
  };

  const renderMessage = (m) => {
    const isAgent = m.user_type === "AGENT" || m.isAgent;
    const canDelete = isAdmin && selected?.status !== "CLOSED" && m.type !== "CALL";
    const attachmentUrl = m.attachment || "";
    const attachmentLabel = m.message || "Attachment";
    const attachmentIsVideo = m.type === "ATTACHMENT" && isVideoMedia(attachmentUrl, attachmentLabel);
    const attachmentIsImage = m.type === "ATTACHMENT" && isImageMedia(attachmentUrl, attachmentLabel);

    return (
      <div key={m.id} className={`flex mb-3 group items-end gap-2 ${isAgent ? "justify-end" : "justify-start"}`}>
        <div className={`relative max-w-[78%] ${isAgent ? "items-end" : "items-start"}`}>
          {canDelete && (
            <button
              type="button"
              onClick={() => deleteMessage(m)}
              className={`absolute -top-2 ${
                isAgent ? "-left-8" : "-right-8"
              } opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 shadow-sm`}
              title="Delete message"
              aria-label="Delete message"
            >
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <div
            className={`rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
              isAgent ? "text-white rounded-tr-sm" : "bg-gray-100 text-gray-800 rounded-tl-sm border border-gray-200"
            }`}
            style={isAgent ? { backgroundColor: THEME.primary } : {}}
          >
          {!isAgent && (
            <p className="text-[10px] font-bold mb-1" style={{ color: THEME.primary }}>
              {m.senderName || "User"}
            </p>
          )}
          {m.type === "TEXT" && <p className="whitespace-pre-wrap break-words">{m.message}</p>}
          {m.type === "IMAGE" && m.image && (
            <button
              type="button"
              onClick={() => openMediaPreview(m.image, "image", m.message || "Image")}
              className="block text-left"
              title="View full screen"
            >
              <img
                src={m.image}
                alt=""
                className="max-h-44 rounded-lg border border-white/20 cursor-zoom-in object-cover"
              />
            </button>
          )}
          {m.type === "ATTACHMENT" && attachmentUrl && attachmentIsVideo && (
            <button
              type="button"
              onClick={() => openMediaPreview(attachmentUrl, "video", attachmentLabel)}
              className={`relative block w-full max-w-[260px] rounded-lg overflow-hidden border ${
                isAgent ? "border-white/25" : "border-gray-300"
              } bg-black/80 text-left`}
              title="Play video"
            >
              <video
                src={attachmentUrl}
                muted
                preload="metadata"
                className="w-full max-h-44 object-cover pointer-events-none"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/35">
                <span className="h-11 w-11 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow">
                  <FiPlay className="w-5 h-5 ml-0.5" />
                </span>
              </span>
              <span
                className={`absolute bottom-1.5 left-1.5 right-1.5 text-[10px] truncate ${
                  isAgent ? "text-white/90" : "text-white"
                }`}
              >
                {attachmentLabel}
              </span>
            </button>
          )}
          {m.type === "ATTACHMENT" && attachmentUrl && attachmentIsImage && (
            <button
              type="button"
              onClick={() => openMediaPreview(attachmentUrl, "image", attachmentLabel)}
              className="block text-left"
              title="View full screen"
            >
              <img
                src={attachmentUrl}
                alt=""
                className="max-h-44 rounded-lg border border-white/20 cursor-zoom-in object-cover"
              />
            </button>
          )}
          {m.type === "ATTACHMENT" &&
            attachmentUrl &&
            !attachmentIsVideo &&
            !attachmentIsImage && (
              <a
                href={attachmentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 underline break-all ${
                  isAgent ? "text-white" : "text-gray-800"
                }`}
              >
                <FiPaperclip className="shrink-0" />
                {attachmentLabel}
              </a>
            )}
          {m.type === "VOICE" && m.voice && (
            <div className="inline-block max-w-full min-w-[12rem]">
              <VoiceNote
                audioUrl={m.voice}
                waveform={m.voice_waveform}
                durationMs={m.voice_duration_ms}
              />
            </div>
          )}
          {m.type === "CALL" && (
            <p className="flex items-center gap-1.5">
              <FiPhone className="shrink-0" /> {m.call_type || "voice"} call
              {m.call_status ? ` · ${m.call_status}` : ""}
              {m.call_duration_seconds
                ? ` (${Math.floor(m.call_duration_seconds / 60)}:${String(
                    m.call_duration_seconds % 60
                  ).padStart(2, "0")})`
                : ""}
            </p>
          )}
          </div>
        </div>
        {isAgent ? (
          <img
            src={NASEEBI_AGENT_AVATAR}
            alt="Naseebi"
            className="w-7 h-7 rounded-full object-cover border border-gray-200 bg-white shrink-0"
          />
        ) : null}
      </div>
    );
  };

  const ticketViews = [
    { id: "available", label: "Available", show: !isAdmin },
    { id: "mine", label: "My Tickets", show: !isAdmin },
    { id: "team", label: "Team Tickets", show: access.isTeamLead || isAdmin },
    { id: "all", label: "All Tickets", show: isAdmin },
  ].filter((v) => v.show !== false);

  const inputCls =
    "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none";

  const listUnreadTotal = tickets.reduce((sum, t) => sum + ticketUnreadCount(t), 0);

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)]">
        {/* Header */}
        <div className="border-l-4 pl-3 mb-6" style={{ borderColor: THEME.primary }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-main text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MdOutlineSupportAgent className="text-2xl" style={{ color: THEME.primary }} />
                Support Center
              </h1>
              <p className="text-gray-500 text-sm mt-1">Manage support tickets and CSR performance</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <TabButton active={mainTab === "tickets"} onClick={() => setMainTab("tickets")}>
                Tickets
              </TabButton>
              {canViewPerformance && (
                <TabButton active={mainTab === "performance"} onClick={() => setMainTab("performance")}>
                  {performanceLoading && mainTab === "performance" ? "Performance…" : "Performance"}
                </TabButton>
              )}
            </div>
          </div>
        </div>

        {/* Tickets tab */}
        {mainTab === "tickets" && (
          <>
            <div className="flex flex-wrap gap-2 mb-4 p-1 bg-gray-50 rounded-xl border border-gray-100 w-fit">
              {ticketViews.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    setTicketView(v.id);
                    setSelected(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                    ticketView === v.id
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 mb-5">
              <select
                className={inputCls}
                value={filters.session_type}
                onChange={(e) => setFilters((f) => ({ ...f, session_type: e.target.value }))}
              >
                <option value="">All users</option>
                <option value="GUEST">Before login</option>
                <option value="AUTHENTICATED">After login</option>
              </select>
              <select
                className={inputCls}
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ))}
              </select>
              <input
                className={`${inputCls} flex-1 min-w-[180px]`}
                placeholder="Search name, email, phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 min-h-[520px]">
              {/* Ticket list */}
              <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
                  <span className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                    <FiHeadphones style={{ color: THEME.primary }} />
                    Requests
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      title="Refresh list"
                      onClick={loadTickets}
                      disabled={loading}
                      className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <FiRefreshCw className={loading ? "animate-spin" : ""} />
                    </button>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {loading ? "…" : tickets.length}
                    </span>
                    {!loading && listUnreadTotal > 0 && (
                        <span
                          className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: THEME.primary }}
                          title="Unread messages"
                        >
                          {listUnreadTotal} unread
                        </span>
                      )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto max-h-[560px]">
                  {loading && <ListSkeleton rows={8} />}
                  {!loading &&
                    tickets.map((t) => (
                      <div
                        key={t.id}
                        className={`px-4 py-3 border-b border-gray-200 transition-colors ${
                          selected?.id === t.id
                            ? "bg-red-50 border-l-4"
                            : "bg-white hover:bg-gray-50 border-l-4 border-l-transparent"
                        }`}
                        style={selected?.id === t.id ? { borderLeftColor: THEME.primary } : {}}
                      >
                        <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (!canReadTicket(t)) {
                              toast.error(
                                t.status === "CLOSED"
                                  ? "Only Admin or Developer can view closed ticket chats."
                                  : t.assigned_agent_id &&
                                      Number(t.assigned_agent_id) !== Number(currentAdminId)
                                    ? "Only Admin or Developer can view another agent's ticket chat."
                                    : "You do not have access to this ticket."
                              );
                              return;
                            }
                            openTicket(t.id);
                          }}
                          disabled={!canReadTicket(t)}
                          className={`flex-1 min-w-0 text-left ${
                            !canReadTicket(t) ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          <div className="flex justify-between gap-2 items-start">
                            <p className="font-semibold text-gray-800 text-sm truncate">
                              {t.displayName || t.guest_name}
                            </p>
                            {ticketUnreadCount(t) > 0 && (
                              <span
                                className="shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                                style={{ backgroundColor: THEME.primary }}
                              >
                                {ticketUnreadCount(t) > 99 ? "99+" : ticketUnreadCount(t)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            #{t.id} · {t.last_message || t.message}
                          </p>
                          <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                            <StatusBadge status={t.status} />
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                                t.session_type === "GUEST"
                                  ? "bg-sky-50 text-sky-700 border-sky-200"
                                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
                              }`}
                            >
                              {t.session_type === "GUEST" ? "Guest" : "Logged in"}
                            </span>
                            <PresenceBadge sessionType={t.session_type} isOnline={t.isOnline} />
                          </div>
                          {t.agentName && (
                            <p className="text-[10px] text-gray-400 mt-1">CSR: {t.agentName}</p>
                          )}
                        </button>
                        {isDeveloper && (
                          <button
                            type="button"
                            title="Delete ticket"
                            onClick={(e) => deleteTicket(t, e)}
                            disabled={deletingTicketId === t.id}
                            className="shrink-0 p-1.5 rounded-lg text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {deletingTicketId === t.id ? (
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                            ) : (
                              <FiTrash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        </div>
                        {ticketView === "available" &&
                          t.status === "PENDING" &&
                          !t.assigned_agent_id && (
                          <button
                            type="button"
                            onClick={() => openTicket(t.id, true)}
                            className="mt-2.5 w-full py-2 rounded-lg text-white text-xs font-semibold shadow-sm hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: THEME.primary }}
                          >
                            Claim & Start
                          </button>
                        )}
                      </div>
                    ))}
                  {!tickets.length && !loading && (
                    <div className="text-center py-16 px-4">
                      <FiHeadphones className="mx-auto text-3xl text-gray-300 mb-2" />
                      <p className="text-gray-500 text-sm">No tickets in this view</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Chat panel */}
              <div className="lg:col-span-3 rounded-xl border border-gray-200 bg-white flex flex-col overflow-hidden shadow-sm">
                {selected ? (
                  <>
                    <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-white to-red-50/40">
                      <div className="flex flex-wrap justify-between gap-3">
                        <div>
                          <p className="font-bold text-gray-900 flex flex-wrap items-center gap-2">
                            {selected.displayName || selected.guest_name || "Support User"}
                            <StatusBadge status={selected.status} />
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                                selected.session_type === "GUEST"
                                  ? "bg-sky-50 text-sky-700 border-sky-200"
                                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
                              }`}
                            >
                              {selected.session_type === "GUEST" ? "Guest" : "Logged in"}
                            </span>
                            <PresenceBadge
                              sessionType={selected.session_type}
                              isOnline={selected.isOnline}
                            />
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            #{selected.id}
                            {(selected.displayEmail || selected.guest_email)
                              ? ` · ${selected.displayEmail || selected.guest_email}`
                              : ""}
                            {(selected.displayPhone || selected.guest_phone)
                              ? ` · ${selected.displayPhone || selected.guest_phone}`
                              : ""}
                          </p>
                          {selected.agentName && (
                            <p className="text-xs text-gray-400 mt-0.5">Assigned to {selected.agentName}</p>
                          )}
                        </div>
                        {canInteractWithTicket && (
                          <div>
                            {!activeCall ? (
                              <button
                                type="button"
                                onClick={startCall}
                                disabled={
                                  startingCall ||
                                  (selected.session_type === "AUTHENTICATED" &&
                                    selected.user == null)
                                }
                                title={
                                  selected.session_type === "AUTHENTICATED" &&
                                  selected.user == null
                                    ? "User is logged out"
                                    : "Call user"
                                }
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[108px] justify-center"
                              >
                                {startingCall ? (
                                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                ) : (
                                  <>
                                    <FiPhone /> Call User
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={endCall}
                                disabled={endingCall}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg text-white font-medium shadow-sm disabled:opacity-70 min-w-[96px] justify-center"
                                style={{ backgroundColor: THEME.primary }}
                              >
                                {endingCall ? (
                                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                ) : (
                                  <>
                                    <FiPhoneOff /> End Call
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {canInteractWithTicket && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {STATUS_OPTIONS.filter((s) => s !== "PENDING").map((s) => {
                            const isActive = selected.status === s;
                            const isLoading = updatingStatus === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => updateStatus(s)}
                                disabled={!!updatingStatus}
                                className={`inline-flex items-center justify-center gap-1.5 text-[10px] px-2.5 py-1 rounded-lg font-medium border transition-colors min-w-[88px] disabled:opacity-70 ${
                                  isActive
                                    ? "text-white border-transparent shadow-sm"
                                    : "bg-white text-gray-600 border-gray-200 hover:border-red-300"
                                }`}
                                style={isActive ? { backgroundColor: THEME.primary } : {}}
                              >
                                {isLoading ? (
                                  <span
                                    className={`inline-block h-3 w-3 animate-spin rounded-full border-2 ${
                                      isActive ? "border-white/40 border-t-white" : "border-gray-300 border-t-gray-600"
                                    }`}
                                  />
                                ) : (
                                  s.replace("_", " ")
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {!canWork && selected?.status === "CLOSED" && isAdmin && (
                        <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mt-3">
                          This ticket is closed. You can read the chat history but cannot send new messages.
                        </p>
                      )}
                      {!canInteractWithTicket && selected?.status !== "CLOSED" && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                          {isAdmin
                            ? "View only — Admin/Developer accounts can monitor tickets but cannot reply, call, or change status."
                            : "View only — claim this ticket to reply or call the user"}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 bg-gray-50/80 min-h-[280px] max-h-[420px] relative">
                      {ticketLoading ? (
                        <ChatSkeleton rows={8} />
                      ) : (
                        <>
                          {messages.length === 0 && (
                            <p className="text-center text-gray-400 text-sm py-8">No messages yet</p>
                          )}
                          {messages.map(renderMessage)}
                          <div ref={bottomRef} />
                        </>
                      )}
                    </div>
                    <div className="p-3 border-t border-gray-200 bg-white">
                      {recording && (
                        <div className="flex min-w-0 items-center gap-3 mb-2 text-sm">
                          <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-500" />
                          <LiveRecordingWaveform bars={waveBars} />
                          <span className="text-gray-500 tabular-nums shrink-0">{formatRecordingTime(recordingTime)}</span>
                          <button
                            type="button"
                            onClick={cancelRecording}
                            disabled={isFinalizing}
                            className="p-1.5 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300"
                            aria-label="Cancel recording"
                          >
                            <FiX />
                          </button>
                          <button
                            type="button"
                            onClick={stopRecording}
                            disabled={isFinalizing}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                            style={{ backgroundColor: THEME.primary }}
                          >
                            {isFinalizing ? (
                              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                            ) : (
                              <>
                                <FiSend /> Send voice
                              </>
                            )}
                          </button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          ref={attachInputRef}
                          type="file"
                          className="hidden"
                          accept="image/*,video/*,.mp4,.mov,.webm,.m4v,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
                          onChange={onAttachSelected}
                        />
                        <input
                          value={text}
                          onChange={(e) => setText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !sending && !recording) sendMessage();
                          }}
                          disabled={!canInteractWithTicket || sending || recording || isFinalizing || ticketLoading}
                          placeholder={
                            canInteractWithTicket
                              ? "Type your reply..."
                              : isAdmin
                                ? "View only — cannot send messages"
                                : "Claim ticket to reply"
                          }
                          className={`flex-1 ${inputCls} disabled:bg-gray-100 disabled:cursor-not-allowed`}
                        />
                        {!recording && (
                          <button
                            type="button"
                            onClick={onAttachClick}
                            disabled={!canInteractWithTicket || sending || isFinalizing || ticketLoading}
                            className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            aria-label="Attach file"
                            title="Attach image or file"
                          >
                            <FiPaperclip />
                          </button>
                        )}
                        {!recording && (
                          <button
                            type="button"
                            onClick={startRecording}
                            disabled={!canInteractWithTicket || sending || isFinalizing || ticketLoading}
                            className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            aria-label="Record voice message"
                          >
                            <FiMic />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={sendMessage}
                          disabled={!canInteractWithTicket || !text.trim() || sending || recording || isFinalizing || ticketLoading}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-semibold text-sm disabled:opacity-40 shadow-sm min-w-[88px] justify-center"
                          style={{ backgroundColor: THEME.primary }}
                        >
                          {sending && !recording ? (
                            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                          ) : (
                            <>
                              <FiSend /> Send
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-8 min-h-[400px]">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${THEME.primary}18` }}
                    >
                      <MdOutlineSupportAgent className="text-3xl" style={{ color: THEME.primary }} />
                    </div>
                    <p className="text-gray-700 font-medium">Select a support request</p>
                    <p className="text-gray-400 text-sm mt-1 max-w-xs">
                      Choose a ticket from the list or claim an available request to start helping users
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Performance tab */}
        {mainTab === "performance" && canViewPerformance && (
          <SupportPerformanceTab
            performance={performance}
            loading={performanceLoading}
            scopeLabel={performanceScope}
            viewScope={performanceViewScope}
            performanceKind={performanceKind}
            isChatSupport={isChatSupport || access?.isChatSupport}
            ledTeams={perfLedTeams}
            selectedTeamId={perfTeamId}
            onTeamSelect={(teamId) => {
              setPerfTeamId(teamId);
              loadPerformance(teamId);
            }}
            isAdmin={isAdmin}
            onViewTicket={(ticketId) => {
              setMainTab("tickets");
              openTicket(ticketId);
            }}
          />
        )}
      </div>

      <SupportCallModal
        open={!!activeCall && callPhase !== "idle"}
        customerName={selected?.displayName || selected?.guest_name || "Customer"}
        phase={
          callPhase === "connected"
            ? "connected"
            : callPhase === "connecting"
              ? "connecting"
              : callPhase === "joining"
                ? "joining"
                : "ringing"
        }
        durationSeconds={callDuration}
        minimized={callMinimized}
        endingCall={endingCall}
        onMinimize={() => setCallMinimized(true)}
        onExpand={() => setCallMinimized(false)}
        onEndCall={endCall}
      />

      {mediaPreview?.url ? (
        <ChatMediaLightbox
          url={mediaPreview.url}
          kind={mediaPreview.kind}
          title={mediaPreview.title}
          onClose={() => setMediaPreview(null)}
        />
      ) : null}

      <MediaSendConfirmModal
        open={!!pendingAttachFile}
        file={pendingAttachFile}
        sending={sending}
        onCancel={() => {
          if (sending) return;
          setPendingAttachFile(null);
        }}
        onConfirm={async () => {
          if (!pendingAttachFile || sending) return;
          const file = pendingAttachFile;
          await sendAttachmentFile(file);
          setPendingAttachFile(null);
        }}
      />
    </div>
  );
}